import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Form,
    Input,
    InputNumber,
    List,
    Modal,
    Pagination,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from "antd";
import { EnvironmentOutlined, MessageOutlined, ShoppingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
    listSentPurchaseRequests,
    listTraderStocks,
    requestPurchase,
    tradingNegotiationApi,
    withdrawPurchaseRequest,
} from "../../servics/trading/tradingApi";
import BuyerNegotiationDrawer from "./negotiation/BuyerNegotiationDrawer";
import { openLoginModal } from "../../store/reducers/authSlice";
import { currentSession } from "../../utils/session";
import { formatMoney } from "../../utils/money";
import { subCategoryLabel } from "../../constants/subCategoryLabels";
import getProductImage, { fallbackToDefaultImage } from "../../utils/getProductImage";
import LoginToUnlock, { isLocked, useLoggedIn } from "./LoginToUnlock";
import { CROP_SUB_CATEGORIES, humanize, productTitle } from "./AvailableProducers";

const { Title, Text, Paragraph } = Typography;

const PAGE_SIZE = 12;

export const PURCHASE_STATUS = {
    REQUESTED: { label: "En attente du négociant", color: "blue" },
    ACCEPTED: { label: "Acceptée", color: "green" },
    REJECTED: { label: "Refusée", color: "red" },
    WITHDRAWN: { label: "Retirée", color: "default" },
};

const quantity = (value, unit) =>
    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(value))} ${unit || ""}`.trim();

const price = (amount, currency, unit) => `${formatMoney(amount, currency?.code)} / ${unit}`;

// Les achats aux négociants portent sur 50 kg au moins (même règle que sales-and-market-tracking).
export const MIN_PURCHASE = { KG: 50, TONNE: 0.05 };

/** Plus petite quantité que l'acheteur peut demander : commande minimale du négociant, et 50 kg. */
export const minimumQuantity = (stock) =>
    Math.max(Number(stock.minOrderQuantity || 0), MIN_PURCHASE[stock.measurementUnit] || 0) || null;

/** Message du backend ({ error, message }) ou repli lisible. */
const backendMessage = (error, fallback) => error?.response?.data?.message || fallback;

/**
 * Négociants : stocks réels mis en vente depuis agro-entreprise-page (sales-and-market-tracking),
 * pour le pays d'activité choisi sur le site, au prix dans la devise de ce pays.
 *
 * Comme le catalogue, mais sans lots de taille fixe : l'acheteur demande la quantité qu'il veut
 * (au moins la commande minimale du négociant, au plus le disponible). Le négociant accepte ou
 * refuse depuis son espace ; l'acheteur suit ses demandes en bas de page. Achats de 50 kg au moins.
 *
 * Tant qu'une demande est en attente, l'acheteur peut la négocier (3 messages par partie, puis
 * offre finale du négociant qu'il accepte ou refuse). Les notifications ouvrent la négociation :
 * ?negotiation=ID&country= (pays du négociant).
 */
const AvailableTraders = () => {
    const dispatch = useDispatch();
    const activityCountry = useSelector((state) => state.country.activityCountry);
    const countryCode = activityCountry?.value;
    const loggedIn = useLoggedIn();

    const [subCategory, setSubCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [sent, setSent] = useState([]);
    const [selectedStock, setSelectedStock] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const requestedQuantity = Form.useWatch("requestedQuantity", form);

    const [negotiation, setNegotiation] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (!countryCode) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        listTraderStocks({ country: countryCode, subCategory, page: page - 1, size: PAGE_SIZE })
            .then((data) => {
                if (!cancelled) setResult(data);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setResult({ items: [], total: 0 });
                setLoadError("Impossible de charger les stocks des négociants pour le moment.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [countryCode, subCategory, page, reloadKey]);

    const loadSent = useCallback(async () => {
        if (!countryCode || !currentSession()) {
            setSent([]);
            return;
        }
        try {
            setSent(await listSentPurchaseRequests(countryCode));
        } catch {
            // simple confort d'affichage : le backend refuse quand même une seconde demande en attente
            setSent([]);
        }
    }, [countryCode]);

    useEffect(() => {
        loadSent();
    }, [loadSent]);

    // Lien de notification : ouvre la négociation de la demande.
    useEffect(() => {
        const id = searchParams.get("negotiation");
        if (!id) return;
        if (!currentSession()) {
            dispatch(openLoginModal());
            return;
        }
        setNegotiation({ id, country: searchParams.get("country") || countryCode });
    }, [searchParams, countryCode, dispatch]);

    const closeNegotiation = () => {
        setNegotiation(null);
        if (searchParams.get("negotiation")) {
            const next = new URLSearchParams(searchParams);
            ["negotiation", "country"].forEach((key) => next.delete(key));
            setSearchParams(next, { replace: true });
        }
    };

    // Une seule demande EN ATTENTE par stock ; une fois servi, l'acheteur peut redemander.
    const pendingRequestFor = (stockId) =>
        sent.find((request) => request.traderStockId === stockId && request.status === "REQUESTED");

    // Connexion vérifiée AVANT d'ouvrir le formulaire (fenêtre de connexion globale de App).
    const openRequest = (stock) => {
        if (!currentSession()) {
            dispatch(openLoginModal());
            return;
        }
        form.resetFields();
        form.setFieldsValue({ requestedQuantity: minimumQuantity(stock) ?? undefined });
        setSelectedStock(stock);
    };

    const submitRequest = async (values) => {
        if (!currentSession()) {
            setSelectedStock(null);
            dispatch(openLoginModal());
            return;
        }
        setSubmitting(true);
        try {
            await requestPurchase(countryCode, selectedStock.id, {
                fullName: values.fullName,
                companyName: values.companyName || undefined,
                telephone: values.telephone || undefined,
                email: values.email || undefined,
                requestedQuantity: values.requestedQuantity,
                desiredDeliveryDate: values.desiredDeliveryDate ? values.desiredDeliveryDate.format("YYYY-MM-DD") : undefined,
                deliveryCity: values.deliveryCity || undefined,
                message: values.message || undefined,
            });
            message.success("Votre demande d'achat a été envoyée au négociant ✅");
            setSelectedStock(null);
            loadSent();
        } catch (error) {
            const status = error?.response?.status;
            const code = error?.response?.data?.error;
            if (status === 401) {
                setSelectedStock(null);
                dispatch(openLoginModal());
            } else if (status === 404 || code === "TRADER_STOCK_NOT_AVAILABLE") {
                setSelectedStock(null);
                message.warning("Ce stock n'est plus en vente.");
                setReloadKey((key) => key + 1);
            } else {
                message.error(backendMessage(error, "Votre demande n'a pas pu être envoyée. Réessayez dans un instant."));
                if (status === 409) {
                    loadSent();
                    if (code === "INSUFFICIENT_STOCK") setReloadKey((key) => key + 1);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const withdraw = async (request) => {
        try {
            await withdrawPurchaseRequest(countryCode, request.id);
            message.success("Demande retirée");
        } catch (error) {
            message.error(backendMessage(error, "La demande n'a pas pu être retirée."));
        }
        loadSent();
    };

    const countryLabel = activityCountry?.label ? ` (${activityCountry.label})` : "";
    const minimumLabel = (stock) =>
        minimumQuantity(stock) ? `Commande minimale : ${quantity(minimumQuantity(stock), stock.measurementUnit)}` : "Toute quantité";

    return (
        <div style={{ padding: 24 }}>
            <Title level={3}>Négociants : stocks disponibles{countryLabel}</Title>
            <Paragraph type="secondary">
                Ces négociants disposent de grands stocks. Indiquez la quantité qu&apos;il vous faut, au prix affiché :
                le négociant vous répond depuis son espace.
            </Paragraph>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                    <Select
                        placeholder="Tous les produits"
                        allowClear
                        style={{ width: "100%" }}
                        value={subCategory}
                        onChange={(value) => {
                            setSubCategory(value ?? null);
                            setPage(1);
                        }}
                        options={CROP_SUB_CATEGORIES.map((code) => ({ value: code, label: humanize(subCategoryLabel(code)) }))}
                        aria-label="Filtrer par produit"
                    />
                </Col>
            </Row>

            {loadError && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="error"
                    showIcon
                    title={loadError}
                    action={<Button size="small" onClick={() => setReloadKey((key) => key + 1)}>Réessayer</Button>}
                />
            )}

            <Spin spinning={loading}>
                {result.items.length === 0 && !loading && !loadError ? (
                    <Empty
                        description={`Aucun stock de négociant n'est en vente${activityCountry?.label ? ` pour ${activityCountry.label}` : ""} pour le moment.`}
                    />
                ) : (
                    <Row gutter={[16, 16]}>
                        {result.items.map((stock, index) => {
                            const pending = pendingRequestFor(stock.id);
                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={stock.id}>
                                    <LoginToUnlock locked={isLocked(loggedIn, page, index)}>
                                        <Card
                                            hoverable
                                            cover={
                                                <img
                                                    alt={productTitle(stock)}
                                                    src={getProductImage(stock.productName, stock.subCategoryName, stock.categoryName)}
                                                    onError={fallbackToDefaultImage}
                                                    style={{ height: 140, objectFit: "cover" }}
                                                />
                                            }
                                            title={<Text strong>{productTitle(stock)}</Text>}
                                        >
                                            <Space direction="vertical" style={{ width: "100%" }} size={4}>
                                                <Text>{stock.companyName}</Text>
                                                <Space wrap size={4}>
                                                    <Tag color="green">{humanize(subCategoryLabel(stock.subCategoryName))}</Tag>
                                                    {stock.city && <Tag icon={<EnvironmentOutlined />} color="blue">{stock.city}</Tag>}
                                                    {stock.quality && <Tag>{stock.quality}</Tag>}
                                                </Space>
                                                <Text>
                                                    <ShoppingOutlined /> {price(stock.unitPrice, stock.currency, stock.measurementUnit)}
                                                </Text>
                                                <Text>📦 {quantity(stock.availableQuantity, stock.measurementUnit)} disponibles</Text>
                                                <Text type="secondary">{minimumLabel(stock)}</Text>
                                                {(stock.origin || stock.harvestYear) && (
                                                    <Text type="secondary">
                                                        {[stock.origin, stock.harvestYear && `récolte ${stock.harvestYear}`].filter(Boolean).join(" · ")}
                                                    </Text>
                                                )}
                                                {stock.description && <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>{stock.description}</Paragraph>}
                                            </Space>
                                            <Button
                                                block
                                                type="primary"
                                                style={{ marginTop: 12 }}
                                                disabled={!!pending}
                                                onClick={() => openRequest(stock)}
                                            >
                                                {pending ? "Demande envoyée ✓" : "Demander une quantité"}
                                            </Button>
                                        </Card>
                                    </LoginToUnlock>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Spin>

            {result.total > PAGE_SIZE && (
                <Row justify="center" style={{ marginTop: 24 }}>
                    <Pagination
                        current={page}
                        pageSize={PAGE_SIZE}
                        total={result.total}
                        onChange={setPage}
                        showSizeChanger={false}
                    />
                </Row>
            )}

            {sent.length > 0 && (
                <Card title="Mes demandes d'achat" style={{ marginTop: 32 }}>
                    <List
                        dataSource={sent}
                        renderItem={(request) => {
                            const status = PURCHASE_STATUS[request.status] || { label: request.status, color: "default" };
                            return (
                                <List.Item
                                    actions={request.status === "REQUESTED" ? [
                                        <Button key="negotiate" size="small" icon={<MessageOutlined />}
                                                onClick={() => setNegotiation({ id: request.id, country: countryCode })}>
                                            Négocier
                                        </Button>,
                                        <Popconfirm
                                            key="withdraw"
                                            title="Retirer cette demande ?"
                                            okText="Retirer"
                                            cancelText="Annuler"
                                            onConfirm={() => withdraw(request)}
                                        >
                                            <Button size="small">Retirer</Button>
                                        </Popconfirm>,
                                    ] : []}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space wrap>
                                                {request.stock ? `${productTitle(request.stock)} — ${request.stock.companyName}` : "Stock de négociant"}
                                                <Tag color={status.color}>{status.label}</Tag>
                                            </Space>
                                        }
                                        description={
                                            <>
                                                <Text>
                                                    {quantity(request.requestedQuantity, request.measurementUnit)} à{" "}
                                                    {price(request.unitPrice, request.currency, request.measurementUnit)}, soit{" "}
                                                    {formatMoney(request.estimatedAmount, request.currency?.code)}
                                                </Text>
                                                {request.decisionNote && (
                                                    <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                                                        Réponse du négociant : {request.decisionNote}
                                                    </Paragraph>
                                                )}
                                            </>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </Card>
            )}

            <BuyerNegotiationDrawer
                api={tradingNegotiationApi}
                requestId={negotiation?.id}
                country={negotiation?.country}
                open={!!negotiation}
                onClose={closeNegotiation}
                onChanged={loadSent}
                title="Négociation de la demande d'achat"
                seller="le négociant"
                agreedText="le négociant a retenu la quantité pour vous."
                sellerPath="/market/trading"
            />

            <Modal
                title={selectedStock ? `Demande d'achat : ${productTitle(selectedStock)}` : ""}
                open={!!selectedStock}
                onCancel={() => setSelectedStock(null)}
                footer={null}
                destroyOnHidden
            >
                {selectedStock && (
                    <>
                        <Paragraph type="secondary">
                            {selectedStock.companyName} vend {quantity(selectedStock.availableQuantity, selectedStock.measurementUnit)} à{" "}
                            {price(selectedStock.unitPrice, selectedStock.currency, selectedStock.measurementUnit)}.{" "}
                            {minimumLabel(selectedStock)}.
                        </Paragraph>
                        <Form form={form} layout="vertical" onFinish={submitRequest}>
                            <Form.Item
                                label={`Quantité souhaitée (${selectedStock.measurementUnit})`}
                                name="requestedQuantity"
                                extra={requestedQuantity > 0
                                    ? `Montant indicatif : ${formatMoney(Number(requestedQuantity) * Number(selectedStock.unitPrice), selectedStock.currency?.code)}`
                                    : undefined}
                                rules={[
                                    { required: true, message: "Indiquez la quantité" },
                                    {
                                        type: "number",
                                        min: minimumQuantity(selectedStock) || 0.01,
                                        max: Number(selectedStock.availableQuantity),
                                        message: minimumQuantity(selectedStock)
                                            ? `Entre ${quantity(minimumQuantity(selectedStock), selectedStock.measurementUnit)} et ${quantity(selectedStock.availableQuantity, selectedStock.measurementUnit)}`
                                            : `Au plus ${quantity(selectedStock.availableQuantity, selectedStock.measurementUnit)}`,
                                    },
                                ]}
                            >
                                <InputNumber style={{ width: "100%" }} min={0} />
                            </Form.Item>
                            <Form.Item label="Nom et prénom" name="fullName" rules={[{ required: true, message: "Indiquez votre nom" }]}>
                                <Input maxLength={255} />
                            </Form.Item>
                            <Form.Item label="Entreprise (facultatif)" name="companyName">
                                <Input maxLength={255} />
                            </Form.Item>
                            <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Téléphone" name="telephone">
                                        <Input maxLength={50} placeholder="Ex : +228 90 00 00 00" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        dependencies={["telephone"]}
                                        rules={[
                                            { type: "email", message: "Email invalide" },
                                            ({ getFieldValue }) => ({
                                                validator: (_, value) =>
                                                    value || getFieldValue("telephone")
                                                        ? Promise.resolve()
                                                        : Promise.reject(new Error("Un téléphone ou un email est nécessaire")),
                                            }),
                                        ]}
                                    >
                                        <Input maxLength={255} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Ville de livraison (facultatif)" name="deliveryCity">
                                        <Input maxLength={100} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Livraison souhaitée (facultatif)" name="desiredDeliveryDate">
                                        <DatePicker
                                            style={{ width: "100%" }}
                                            format="DD/MM/YYYY"
                                            disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Message au négociant (facultatif)" name="message">
                                <Input.TextArea rows={3} maxLength={4000} placeholder="Usage prévu, conditionnement, enlèvement…" />
                            </Form.Item>
                            <div style={{ textAlign: "right" }}>
                                <Button onClick={() => setSelectedStock(null)} style={{ marginRight: 8 }}>Annuler</Button>
                                <Button type="primary" htmlType="submit" loading={submitting}>Envoyer la demande</Button>
                            </div>
                        </Form>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default AvailableTraders;
